import { type FC, useEffect, useEffectEvent } from "react";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { localStorageService } from "../services/LocalStorageService";
import { resolveDepartmentLookAction } from "./departmentLooks";
import { readStoredColor, readStoredLogo, useThemePreference } from "./ThemePreferenceProvider";
import { DEFAULT_LOGO } from "./tokens";

/**
 * Saves a department's look (logo + primary color) when the user opens it, and removes it again
 * when they move to a department without one. The look lives in the normal theme storage, so the
 * sign-in pages show it after logout too. Renders nothing.
 */
export const DepartmentLookSync: FC = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { setLogo, setColor, resetColor } = useThemePreference();
	const departmentId = chosenDepartment.department_id;

	// An effect event, so a logo or color change by the user does not re-run the check.
	// It only runs when the department changes.
	const syncLook = useEffectEvent((currentDepartmentId: string) => {
		// Read everything from storage, not React state: another tab may have changed it.
		const action = resolveDepartmentLookAction({
			departmentId: currentDepartmentId,
			logo: readStoredLogo(),
			primary: readStoredColor("primary"),
			appliedFor: localStorageService.getDepartmentLookAppliedFor(),
		});

		// Changes still go through the provider, so the page updates right away.
		if (action.type === "apply") {
			setLogo(action.look.logo);
			setColor("primary", action.look.primary);
			localStorageService.setDepartmentLookAppliedFor(currentDepartmentId);
		} else if (action.type === "revert") {
			setLogo(DEFAULT_LOGO);
			resetColor("primary");
			localStorageService.removeDepartmentLookAppliedFor();
		} else if (action.type === "release") {
			localStorageService.removeDepartmentLookAppliedFor();
		}
	});

	useEffect(() => {
		syncLook(departmentId);
	}, [departmentId]);

	return null;
};
